import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import { formatApiErrorDetail } from "../api";
import PasswordInput from "../PasswordInput";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: "entrepreneur", tKey: "auth.roleEntrepreneur" },
  { value: "advisor", tKey: "auth.roleAdvisor" },
  { value: "provider", tKey: "auth.roleProvider" },
  { value: "investor", tKey: "auth.roleInvestor" },
  { value: "accelerator", tKey: "auth.roleAccelerator" },
];

export default function Register() {
  const { t, lang, setLang } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/app";
  const [form, setForm] = useState({
    name: "",
    email: params.get("email") || "",
    password: "",
    role: params.get("role") || "entrepreneur",
    company: "",
    note: "",
    lang,
  });
  const [consent, setConsent] = useState({ terms: false, privacy: false, beta: false });
  const [legalVersions, setLegalVersions] = useState({ terms: "1.0", privacy: "1.0", beta: "1.0" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [signupMode, setSignupMode] = useState("waitlist");
  const [waitlisted, setWaitlisted] = useState(null); // {email, already} on success

  useEffect(() => {
    axios.get(`${API_BASE}/api/legal`)
      .then((r) => {
        const v = { terms: "1.0", privacy: "1.0", beta: "1.0" };
        (r.data.items || []).forEach((it) => {
          if (v[it.slug] !== undefined) v[it.slug] = it.version;
        });
        setLegalVersions(v);
      })
      .catch(() => { /* non-fatal, keep defaults */ });
    axios.get(`${API_BASE}/api/auth/signup-mode`)
      .then((r) => setSignupMode(r.data?.mode || "waitlist"))
      .catch(() => { /* keep default */ });
  }, []);

  const onChange = (k) => (e) => {
    const value = e.target.value;
    setForm({ ...form, [k]: value });
    if (k === "lang") setLang(value);
  };
  const pickLang = (next) => {
    setLang(next);
    setForm((f) => ({ ...f, lang: next }));
  };
  const onConsent = (k) => (e) => setConsent({ ...consent, [k]: e.target.checked });

  const allConsented = consent.terms && consent.privacy && consent.beta;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!allConsented) {
      setErr(t("auth.consentRequired"));
      return;
    }
    setLoading(true);
    try {
      const resp = await register({
        ...form,
        email: form.email.trim(),
        consent_terms: consent.terms,
        consent_privacy: consent.privacy,
        consent_beta: consent.beta,
        consent_versions: legalVersions,
      });
      if (resp && resp.waitlisted) {
        setWaitlisted({ email: resp.email || form.email.trim(), already: !!resp.already });
      } else {
        setLang(form.lang);
        const dest = form.role === "advisor" && !params.get("next")
          ? "/app/settings?compose=linkedin"
          : nextPath;
        navigate(dest);
      }
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const legalLink = (slug, label) => (
    <a
      href={`/legal/${slug}`}
      target="_blank"
      rel="noopener noreferrer"
      className="tb-link"
      data-testid={`register-legal-link-${slug}`}
    >
      {label}
    </a>
  );

  if (waitlisted) {
    return (
      <div className="tb-app tb-auth-page">
        <Link to="/" className="tb-app-logo">
          Traction<span>Board</span>
        </Link>
        <div className="tb-form" data-testid="register-waitlisted">
          <h1 className="tb-h1" style={{ marginBottom: "0.5rem", fontSize: "1.4rem" }}>
            {t("auth.waitlistTitle")}
          </h1>
          <p style={{ color: "var(--text)" }}>
            {t("auth.waitlistBody", { email: waitlisted.email })}
          </p>
          {waitlisted.already && (
            <p className="tb-muted" data-testid="register-waitlisted-already">
              {t("auth.waitlistAlready")}
            </p>
          )}
          <button
            type="button"
            className="tb-primary-btn"
            data-testid="waitlisted-back-login"
            onClick={() => navigate("/login")}
          >
            {t("auth.switchToLogin")}
          </button>
          <div className="tb-lang-pick" style={{ justifySelf: "center" }}>
            <button type="button" className={lang === "en" ? "on" : ""} onClick={() => pickLang("en")} data-testid="register-lang-en">EN</button>
            <button type="button" className={lang === "fr" ? "on" : ""} onClick={() => pickLang("fr")} data-testid="register-lang-fr">FR</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tb-app tb-auth-page">
      <Link to="/" className="tb-app-logo">
        Traction<span>Board</span>
      </Link>
      <form className="tb-form" onSubmit={submit} data-testid="register-form">
        <h1 className="tb-h1" style={{ marginBottom: "0.5rem", fontSize: "1.4rem" }}>
          {t("auth.registerTitle")}
        </h1>
        {signupMode === "waitlist" && (
          <div className="tb-invite-only-note" data-testid="register-invite-only-note">
            {t("auth.inviteOnlyNote")}
          </div>
        )}
        <div>
          <label>{t("auth.name")}</label>
          <input type="text" required value={form.name} onChange={onChange("name")} data-testid="register-name" />
        </div>
        <div>
          <label>{t("auth.email")}</label>
          <input type="email" required value={form.email} onChange={onChange("email")} data-testid="register-email" autoComplete="email" />
        </div>
        <div>
          <label>{t("auth.password")}</label>
          <PasswordInput
            value={form.password}
            onChange={onChange("password")}
            required
            minLength={6}
            testid="register-password"
            autoComplete="new-password"
          />
        </div>
        <div>
          <label>{t("auth.company")}</label>
          <input type="text" value={form.company} onChange={onChange("company")} data-testid="register-company" />
        </div>
        <div>
          <label>{t("auth.role")}</label>
          <select value={form.role} onChange={onChange("role")} data-testid="register-role">
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {t(r.tKey)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>{t("auth.lang")}</label>
          <select value={form.lang} onChange={onChange("lang")} data-testid="register-lang">
            <option value="en">{t("auth.langEn")}</option>
            <option value="fr">{t("auth.langFr")}</option>
          </select>
        </div>
        {signupMode === "waitlist" && (
          <div>
            <label>{t("auth.noteLabel")}</label>
            <textarea
              value={form.note}
              onChange={onChange("note")}
              maxLength={1000}
              rows={3}
              placeholder={t("auth.notePlaceholder")}
              data-testid="register-note"
            />
          </div>
        )}

        <div className="tb-consent-block tb-consent-block-slim" data-testid="register-consents">
          <label className="tb-consent-row">
            <input
              type="checkbox"
              checked={consent.terms && consent.privacy}
              onChange={(e) => setConsent({ ...consent, terms: e.target.checked, privacy: e.target.checked })}
              required
              data-testid="register-consent-terms-privacy"
            />
            <span>
              {t("auth.consentTermsPrefix")} {legalLink("terms", t("auth.consentTermsLink"))} {t("auth.consentAnd")} {legalLink("privacy", t("auth.consentPrivacyLink"))}.
            </span>
          </label>
          <label className="tb-consent-row">
            <input
              type="checkbox"
              checked={consent.beta}
              onChange={onConsent("beta")}
              required
              data-testid="register-consent-beta"
            />
            <span>
              {t("auth.consentBetaPrefix")} {legalLink("beta", t("auth.consentBetaLink"))}.
            </span>
          </label>
        </div>

        {err && <div className="tb-form-error" data-testid="register-error">{err}</div>}
        <button
          type="submit"
          className="tb-primary-btn"
          disabled={loading || !allConsented}
          data-testid="register-submit"
        >
          {loading
            ? t("auth.loading")
            : signupMode === "waitlist"
              ? t("auth.submitRequestAccess")
              : t("auth.submitRegister")}
        </button>
        <div className="tb-lang-pick" style={{ justifySelf: "center" }}>
          <button type="button" className={lang === "en" ? "on" : ""} onClick={() => pickLang("en")} data-testid="register-lang-en">EN</button>
          <button type="button" className={lang === "fr" ? "on" : ""} onClick={() => pickLang("fr")} data-testid="register-lang-fr">FR</button>
        </div>
      </form>
      <div className="tb-auth-switch">
        <Link to="/login" data-testid="link-login">{t("auth.switchToLogin")}</Link>
        <span className="tb-muted"> · </span>
        <Link to="/legal" className="tb-link" data-testid="link-legal-center">
          {t("legal.centerTitle")}
        </Link>
      </div>
    </div>
  );
}
